<template>
  <ProfileSection
    :user="user"
    :copyright-owners="copyrightOwners"
    :copyright-owners-left="copyrightOwnersLeft"
    :origin-flag-enabled="originFlagEnabled"
    :identity-unverified="identityUnverified"
    :identity-processing="identityProcessing"
    :identity-loading="identityLoading"
    @open-dialog="handleOpenDialog"
    @verify-identity="handleVerifyIdentity"
  />

  <ContactSection :user="user" @open-dialog="handleOpenDialog" />

  <IdentitySection
    :user="user"
    :date-format="dateFormat"
    :automatic-timezone="automaticTimezone"
    :current-timezone-label="currentTimezoneLabel"
    :custom-timezone="customTimezone"
    :country-options="countryOptions"
    :timezone-options="getTimezonesForCountry(customTimezone.country)"
    :is-mobile="device.isMobile.value"
    @open-dialog="handleOpenDialog"
    @update-date-format="handleDateFormatChange"
    @update-automatic-timezone="handleAutomaticTimezoneChange"
    @update-custom-timezone-country="handleCustomTimezoneCountryChange"
    @update-custom-timezone-name="handleCustomTimezoneNameChange"
  />

  <DangerZoneSection :user="user" @delete="handleDeleteClick" />
</template>

<script setup lang="ts">
import ProfileSection from '@/features/settings/components/ProfileSection.vue'
import ContactSection from '@/features/settings/components/ContactSection.vue'
import IdentitySection from '@/features/settings/components/IdentitySection.vue'
import DangerZoneSection from '@/features/settings/components/DangerZoneSection.vue'
import { ref, watch, computed } from 'vue'
import { useUserStore } from '@/shared/stores/user'
import {
  useCopyrightOwnersQuery,
  useUpdateAccountDateFormatMutation,
  useUpdateAccountTimezoneMutation,
  VerificationStatus,
  type TimezoneInput,
} from '@/shared/graphql/legacy/generated'
import { graphql } from '@/shared/graphql/router'
import { IdentityVerificationType } from '@/shared/graphql/router/graphql'
import { useMutation } from '@vue/apollo-composable'
import { getTimeZones } from '@vvo/tzdb'
import { useDevice } from '@/shared/composables/useDevice'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { useHead } from '@unhead/vue'
import { getStripe } from '@/shared/services/stripe'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

useHead({ title: 'Account - Dacr' })

const { result: copyrightOwnersQuery } = useCopyrightOwnersQuery()
const { mutate: updateAccountDateFormat } = useUpdateAccountDateFormatMutation()
const { mutate: updateAccountTimezone, loading } =
  useUpdateAccountTimezoneMutation()

const { mutate: verifyIdentity } = useMutation(
  graphql(`
    mutation VerifyIdentity($input: VerifyIdentityInput!) {
      verifyIdentity(input: $input) {
        status
        clientSecret
        sessionId
        message
        verifiedAt
      }
    }
  `),
  { clientId: 'router' }
)

const device = useDevice()
const dialog = useDialog()
const userStore = useUserStore()
const webviewStore = useWebviewStore()
const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const user = computed(() => userStore.user)

const copyrightOwners = computed(
  () => copyrightOwnersQuery.value?.copyrightOwners.items
)

const copyrightOwnersLeft = computed(() => {
  const remainingCount = (copyrightOwners.value?.length || 0) - 5
  if (remainingCount <= 0) return undefined
  const ownersText = remainingCount > 1 ? 'owners' : 'owner'
  return `${remainingCount} more ${ownersText}`
})

const identityLoading = ref(false)

const identityUnverified = computed(
  () => userStore.user?.verificationStatus === VerificationStatus.None
)

const identityProcessing = computed(
  () => userStore.user?.verificationStatus === VerificationStatus.Processing
)

const handleVerifyIdentity = async () => {
  identityLoading.value = true

  const result = await verifyIdentity({
    input: { type: IdentityVerificationType.Document },
  })

  const clientSecret = result?.data?.verifyIdentity.clientSecret
  if (!clientSecret) return

  const stripe = await getStripe()
  if (!stripe) return

  await stripe.verifyIdentity(clientSecret)
  identityLoading.value = false

  if (userStore.user) {
    userStore.user.verificationStatus = VerificationStatus.Processing
  }
}

const handleOpenDialog = (name: string) => {
  const dialogName = name as keyof typeof Dialogs
  if (!Dialogs[dialogName]) return

  dialog.open(Dialogs[dialogName], {
    props: { user: user.value },
  })
}

// Date format
const dateFormat = computed(() => user.value?.dateFormat || '')

const handleDateFormatChange = (value: string) => {
  if (!user.value) return

  updateAccountDateFormat(
    { dateFormat: value },
    {
      optimisticResponse: {
        __typename: 'Mutation',
        updateAccountDateFormat: {
          __typename: 'User',
          id: user.value.id,
          dateFormat: value,
        },
      },
      update() {
        if (webviewStore.isActive) {
          webviewStore.postMessage({
            type: NativeActionsEnum.UserUpdated,
            data: { user: { ...user.value, dateFormat: value } },
          })
        }
      },
    }
  )
}

// Timezone
const timezones = getTimeZones()

const currentTimezone = computed(() => {
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone
  return timezones.find(
    (tz) => tz.name === timezoneName || tz.group.includes(timezoneName)
  )
})

const updateTimezone = (newTimezone: TimezoneInput | null) => {
  if (!user.value) return

  updateAccountTimezone(
    { timezone: newTimezone },
    {
      optimisticResponse: {
        __typename: 'Mutation',
        updateAccountTimezone: {
          __typename: 'User',
          id: user.value.id,
          timezone: newTimezone
            ? { __typename: 'Timezone', ...newTimezone }
            : null,
        },
      },
      update() {
        if (webviewStore.isActive) {
          webviewStore.postMessage({
            type: NativeActionsEnum.UserUpdated,
            data: {
              user: {
                ...user.value,
                timezone: newTimezone
                  ? { __typename: 'Timezone' as const, ...newTimezone }
                  : null,
              },
            },
          })
        }
      },
    }
  )
}

const automaticTimezone = computed(() => !user.value?.timezone)

const handleAutomaticTimezoneChange = (value: boolean) => {
  updateTimezone(
    value
      ? null
      : {
          name: currentTimezone.value?.name || '',
          country: currentTimezone.value?.countryName || '',
        }
  )
}

const currentTimezoneLabel = computed(() => {
  if (!currentTimezone.value) return undefined
  const offsetInHours = currentTimezone.value.currentTimeOffsetInMinutes / 60
  return `${currentTimezone.value.abbreviation} (UTC+${offsetInHours})`.replace(
    '+-',
    '-'
  )
})

const customTimezone = ref<TimezoneInput>({
  country:
    user.value?.timezone?.country || currentTimezone.value?.countryName || '',
  name: user.value?.timezone?.name || currentTimezone.value?.name || '',
})

watch(user, () => {
  if (!user.value?.timezone) return
  const { __typename, ...userTimezone } = user.value.timezone
  customTimezone.value = userTimezone
})

watch(
  customTimezone,
  () => {
    if (user.value?.timezone?.country !== customTimezone.value.country) {
      const available = getTimezonesForCountry(customTimezone.value.country)
      if (available.length) {
        customTimezone.value.name = available[0].value
      }
    }

    if (
      !loading.value &&
      customTimezone.value.country &&
      customTimezone.value.name &&
      (user.value?.timezone?.name !== customTimezone.value.name ||
        user.value?.timezone?.country !== customTimezone.value.country)
    ) {
      updateTimezone(customTimezone.value)
    }
  },
  { deep: true }
)

const handleCustomTimezoneCountryChange = (value: string) => {
  customTimezone.value = { ...customTimezone.value, country: value }
}

const handleCustomTimezoneNameChange = (value: string) => {
  customTimezone.value = { ...customTimezone.value, name: value }
}

const countries = timezones.reduce(
  (acc, tz) => {
    acc[tz.countryName] = acc[tz.countryName] || []
    acc[tz.countryName].push({
      abbreviation: tz.abbreviation,
      offset: tz.currentTimeOffsetInMinutes,
      name: tz.name,
    })
    return acc
  },
  {} as Record<string, { abbreviation: string; offset: number; name: string }[]>
)

const countryOptions = Object.keys(countries)
  .map((country) => ({ label: country, value: country }))
  .sort((a, b) => a.label.localeCompare(b.label))

const getTimezonesForCountry = (country: string) => {
  if (!countries[country]) return []

  const timezonesForCountry = countries[country].filter((tz, index, arr) => {
    return arr.findIndex((tz2) => tz.offset === tz2.offset) === index
  })

  return timezonesForCountry.map((tz) => ({
    label: `${tz.abbreviation} (UTC+${tz.offset / 60})`.replace('+-', '-'),
    value: tz.name,
  }))
}

const handleDeleteClick = () => {
  dialog.open(Dialogs.DeactivateAccountPrompt)
}
</script>
